import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { cn } from '../../lib/utils';
import { Plug, ChevronDown, Loader2, ExternalLink, RefreshCw, Unplug } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE_URL } from '../../config/api';

const ConnectedAccountsSection = ({ colors, user }) => {
  const [adoStatus, setAdoStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('session_token');
      const resp = await fetch(`${API_BASE_URL}/api/integrations/azure-devops/connection`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (resp.ok) {
        setAdoStatus(await resp.json());
      }
    } catch (err) {
      console.error('Failed to fetch ADO status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Check for callback params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('ado_connected') === 'true') {
      toast.success('Azure DevOps connected successfully');
      fetchStatus();
      window.history.replaceState({}, '', window.location.pathname + '?tab=profile');
    }
    if (params.get('ado_error')) {
      toast.error(`Azure DevOps connection failed: ${params.get('ado_error')}`);
      window.history.replaceState({}, '', window.location.pathname + '?tab=profile');
    }
  }, [fetchStatus]);

  const handleConnect = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const resp = await fetch(`${API_BASE_URL}/api/integrations/azure-devops/auth-url`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Failed to get auth URL');
      const { auth_url } = await resp.json();
      window.location.href = auth_url;
    } catch (err) {
      toast.error(err.message || 'Failed to initiate connection');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Azure DevOps? Products using this connection will lose sync.')) return;
    setDisconnecting(true);
    try {
      const token = localStorage.getItem('session_token');
      const resp = await fetch(`${API_BASE_URL}/api/integrations/azure-devops/disconnect`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Failed to disconnect');
      toast.success('Azure DevOps disconnected');
      setAdoStatus({ connected: false });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDisconnecting(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('session_token');
      const resp = await fetch(`${API_BASE_URL}/api/integrations/azure-devops/refresh-data`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Failed to refresh');
      const data = await resp.json();
      toast.success(`Refreshed: ${data.projects} projects, ${data.repos} repos`);
      fetchStatus();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRefreshing(false);
    }
  };

  const isConnected = adoStatus?.connected;
  const isExpired = adoStatus?.status === 'expired';

  return (
    <Collapsible defaultOpen={true}>
      <Card className={cn("border", colors.bgCard, colors.borderPrimary)} data-testid="connected-accounts-section">
        <CollapsibleTrigger asChild>
          <div className={cn("group w-full p-6 flex items-center justify-between text-left cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors rounded-t-lg")}>
            <div className="flex items-center gap-3">
              <Plug className={cn("w-5 h-5", colors.textAccent)} />
              <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>Connected Accounts</h3>
            </div>
            <ChevronDown className={cn("w-5 h-5 transition-transform duration-200 group-data-[state=closed]:-rotate-90", colors.textMuted)} />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-6 pb-6">
            <p className={cn("text-sm mb-4", colors.textMuted)}>
              Connect your external development accounts to enable Source Control and Ticketing integrations on your products.
            </p>

            {/* Azure DevOps Card */}
            <div className={cn("p-4 rounded-lg border", colors.bgTertiary, colors.borderPrimary)} data-testid="ado-connection-card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor">
                      <path d="M0 8.877L2.247 5.91l8.405-3.416V.022l7.37 5.393L2.966 8.338v8.225L0 15.707zm24-4.45v15.166l-5.624 4.385-8.97-3.26.058-.064 8.97-2.645V5.27z" />
                    </svg>
                  </div>
                  <div>
                    <p className={cn("font-medium", colors.textPrimary)}>
                      Microsoft / Azure DevOps
                    </p>
                    {loading ? (
                      <p className={cn("text-sm", colors.textMuted)}>Checking connection...</p>
                    ) : isConnected ? (
                      <p className={cn("text-sm text-green-500")}>
                        Connected as {adoStatus.display_name || adoStatus.email}
                      </p>
                    ) : isExpired ? (
                      <p className="text-sm text-amber-500">
                        Connection expired — please re-authorize
                      </p>
                    ) : (
                      <p className={cn("text-sm", colors.textMuted)}>
                        Authorize to access your Azure DevOps projects and repos
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {loading ? (
                    <Loader2 className={cn("w-4 h-4 animate-spin", colors.textMuted)} />
                  ) : isConnected ? (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleRefresh}
                        disabled={refreshing}
                        data-testid="ado-refresh-btn"
                      >
                        <RefreshCw className={cn("w-4 h-4", refreshing ? "animate-spin" : "", colors.textMuted)} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleDisconnect}
                        disabled={disconnecting}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20"
                        data-testid="ado-disconnect-btn"
                      >
                        {disconnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unplug className="w-4 h-4" />}
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      onClick={handleConnect}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="ado-connect-btn"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Connect
                    </Button>
                  )}
                </div>
              </div>

              {/* Connected details */}
              {isConnected && adoStatus.organizations?.length > 0 && (
                <div className={cn("mt-3 pt-3 border-t text-sm", colors.borderPrimary)}>
                  <div className="flex flex-wrap gap-4">
                    <span className={colors.textMuted}>
                      <strong className={colors.textSecondary}>{adoStatus.organizations.length}</strong> org{adoStatus.organizations.length !== 1 ? 's' : ''}
                    </span>
                    <span className={colors.textMuted}>
                      <strong className={colors.textSecondary}>{adoStatus.projects?.length || 0}</strong> project{(adoStatus.projects?.length || 0) !== 1 ? 's' : ''}
                    </span>
                    <span className={colors.textMuted}>
                      <strong className={colors.textSecondary}>{adoStatus.repos?.length || 0}</strong> repo{(adoStatus.repos?.length || 0) !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {adoStatus.organizations.map(org => (
                    <div key={org.id || org.name} className={cn("mt-1 text-xs", colors.textMuted)}>
                      {org.name} — {org.url}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default ConnectedAccountsSection;
